/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Create / edit a mortgage application — POST /re/mortgage-applications, PUT /re/mortgage-applications/{id}.
 * LTV, EMI and DBR are shown live for guidance; the backend stores ltv_pct/dbr_pct and returns warnings.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem, TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { ContactPicker, RePicker, UserPicker } from './Pickers';
import { KV } from './Widgets';
import {
  EMPLOYMENT_TYPES, RATE_TYPES, RESIDENCY_STATUSES, computeDbr, computeEmi, computeLtv, errorMessage, formatMoney,
  formatPct, humanize, toNumber, useReApi, useReGet,
} from '../api';
import { buyerCategoryOptions, defaultCurrency, isAustralia } from '../../variant';

const FIELDS_COMMON = [
  'applicant_contact_id', 'co_applicant_contact_id', 'booking_id', 'unit_id', 'lender_id',
  'employment_type', 'monthly_income', 'existing_liabilities', 'property_value', 'loan_amount', 'tenure_months',
  'rate_type', 'interest_rate_pct', 'bank_reference', 'advisor_fee_amount', 'assigned_to', 'notes',
];
// UAE asks residency (it sets the Central Bank caps); Australia asks the
// buyer category (FIRB) and the broker's credit licence.
const fieldsFor = () => (isAustralia()
  ? [...FIELDS_COMMON, 'buyer_category', 'credit_licence_no']
  : [...FIELDS_COMMON.slice(0, 5), 'residency_status', ...FIELDS_COMMON.slice(5)]);
const NUMERIC = new Set(['monthly_income', 'existing_liabilities', 'property_value', 'loan_amount', 'tenure_months', 'interest_rate_pct', 'advisor_fee_amount']);

export const WARNING_TEXT = {
  DBR_ABOVE_50: 'Debt burden ratio is above the UAE Central Bank cap of 50%.',
  LTV_ABOVE_LIMIT: 'Loan-to-value is above the lender / Central Bank limit for this applicant.',
  LTV_ABOVE_80: 'Loan-to-value is above 80%.',
  LTV_ABOVE_60: 'Loan-to-value is above 60%.',
  LMI_LIKELY: 'LVR is above 80%: lenders usually charge lenders mortgage insurance.',
};

/** Default max LTV when the lender has none: UAE Central Bank, first home ≤ AED 5M. */
export const defaultMaxLtv = (residency) => (residency === 'NON_RESIDENT' ? 60 : 80);

export function MortgageMetrics({ values, lender }) {
  const emi = computeEmi(values.loan_amount, values.interest_rate_pct, values.tenure_months);
  const ltv = computeLtv(values.loan_amount, values.property_value);
  const dbr = computeDbr(values.monthly_income, values.existing_liabilities, emi);
  const maxLtv = toNumber(values.residency_status === 'NON_RESIDENT' ? lender?.max_ltv_non_resident_pct : lender?.max_ltv_resident_pct)
    ?? defaultMaxLtv(values.residency_status);
  if (isAustralia()) {
    // No regulatory caps: show the LVR with the LMI line, and repayments as a share of income.
    return (
      <MDBox>
        <KV label="Monthly repayment" value={emi ? formatMoney(emi, defaultCurrency()) : '—'} bold />
        <KV label="LVR" value={ltv != null ? formatPct(ltv, 1) : '—'} color={ltv != null && ltv > 80 ? '#8A5300' : null} bold />
        <KV label="Repayments / income" value={dbr != null ? formatPct(dbr, 1) : '—'} bold />
        {ltv != null && ltv > 80 && <Alert severity="info" sx={{ mt: 1 }}>{WARNING_TEXT.LMI_LIKELY}</Alert>}
      </MDBox>
    );
  }
  return (
    <MDBox>
      <KV label="Monthly instalment (EMI)" value={emi ? formatMoney(emi, defaultCurrency()) : '—'} bold />
      <KV label={`LTV (max ${formatPct(maxLtv, 0)})`} value={ltv != null ? formatPct(ltv, 1) : '—'} color={ltv != null && ltv > maxLtv ? '#BA1A1A' : null} bold />
      <KV label="DBR (cap 50%)" value={dbr != null ? formatPct(dbr, 1) : '—'} color={dbr != null && dbr > 50 ? '#BA1A1A' : null} bold />
      {dbr != null && dbr > 50 && <Alert severity="warning" sx={{ mt: 1 }}>{WARNING_TEXT.DBR_ABOVE_50}</Alert>}
      {ltv != null && ltv > maxLtv && <Alert severity="warning" sx={{ mt: 1 }}>{`LTV ${formatPct(ltv, 1)} exceeds the ${formatPct(maxLtv, 0)} limit for a ${humanize(values.residency_status || 'RESIDENT').toLowerCase()} applicant.`}</Alert>}
    </MDBox>
  );
}

MortgageMetrics.propTypes = { values: PropTypes.object.isRequired, lender: PropTypes.object };
MortgageMetrics.defaultProps = { lender: null };

export default function MortgageForm({ open, application, onClose, onSaved }) {
  const { api } = useReApi();
  const [v, setV] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const lender = useReGet(v.lender_id ? `/re/lenders/${v.lender_id}` : null);
  const isEdit = Boolean(application?.id);

  useEffect(() => {
    if (!open) return;
    const init = {};
    fieldsFor().forEach((f) => { init[f] = application?.[f] ?? (NUMERIC.has(f) || f === 'notes' || f === 'bank_reference' || f === 'credit_licence_no' ? '' : null); });
    if (!application) {
      if (isAustralia()) init.buyer_category = 'CITIZEN';
      else init.residency_status = 'RESIDENT';
      init.employment_type = 'SALARIED';
      init.rate_type = 'FIXED';
      init.tenure_months = '300';
    }
    Object.keys(init).forEach((k) => { if (NUMERIC.has(k) && init[k] !== '' && init[k] !== null) init[k] = String(init[k]); });
    setV(init);
    setErr(null);
  }, [open, application]);

  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val && val.target ? val.target.value : val }));

  const submit = async () => {
    if (!v.applicant_contact_id) {
      setErr('Choose the applicant.');
      return;
    }
    setSaving(true);
    const body = {};
    fieldsFor().forEach((f) => {
      if (NUMERIC.has(f)) body[f] = toNumber(v[f]);
      else body[f] = v[f] === '' ? null : v[f];
    });
    if (body.tenure_months != null) body.tenure_months = Math.round(body.tenure_months);
    try {
      const res = isEdit
        ? await api.put(`/re/mortgage-applications/${application.id}`, body)
        : await api.post('/re/mortgage-applications', body);
      onSaved(res.data);
    } catch (e) {
      setErr(errorMessage(e, 'Could not save the application'));
    } finally {
      setSaving(false);
    }
  };

  const money = (k, label) => (
    <TextField
      fullWidth size="small" type="number" label={label} value={v[k] ?? ''} onChange={set(k)}
      InputProps={{ startAdornment: <InputAdornment position="start">{defaultCurrency()}</InputAdornment> }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{isEdit ? `Edit ${application.application_number || 'application'}` : 'New mortgage application'}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={3} sx={{ pt: 1 }}>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><ContactPicker label="Applicant" value={v.applicant_contact_id} onChange={set('applicant_contact_id')} allowCreate required sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={6}><ContactPicker label="Co-applicant" value={v.co_applicant_contact_id} onChange={set('co_applicant_contact_id')} sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={4}>
                {isAustralia() ? (
                  <TextField select fullWidth size="small" label="Buyer" value={v.buyer_category || ''} onChange={set('buyer_category')}>
                    {buyerCategoryOptions().map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                ) : (
                  <TextField select fullWidth size="small" label="Residency" value={v.residency_status || ''} onChange={set('residency_status')}>
                    {RESIDENCY_STATUSES.map((r) => <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>)}
                  </TextField>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth size="small" label="Employment" value={v.employment_type || ''} onChange={set('employment_type')}>
                  {EMPLOYMENT_TYPES.map((r) => <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}><UserPicker label="Advisor" value={v.assigned_to} onChange={set('assigned_to')} emptyLabel="Not set" sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={4}>{money('monthly_income', 'Monthly income')}</Grid>
              <Grid item xs={12} md={4}>{money('existing_liabilities', 'Existing monthly liabilities')}</Grid>
              <Grid item xs={12} md={4}>{money('property_value', 'Property value')}</Grid>
              <Grid item xs={12} md={4}>{money('loan_amount', 'Loan amount')}</Grid>
              <Grid item xs={6} md={2}><TextField fullWidth size="small" type="number" label="Tenure (months)" value={v.tenure_months ?? ''} onChange={set('tenure_months')} /></Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth size="small" type="number" label="Rate" value={v.interest_rate_pct ?? ''} onChange={set('interest_rate_pct')} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField select fullWidth size="small" label="Rate type" value={v.rate_type || ''} onChange={set('rate_type')}>
                  {RATE_TYPES.map((r) => <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}><RePicker path="/re/lenders" label="Lender" value={v.lender_id} onChange={set('lender_id')} params={{ is_active: true }} sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Bank reference" value={v.bank_reference || ''} onChange={set('bank_reference')} /></Grid>
              <Grid item xs={12} md={6}><RePicker path="/re/bookings" label="Booking (optional)" value={v.booking_id} onChange={set('booking_id')} getLabel={(b) => [b.booking_number, b.unit_number, b.project_name].filter(Boolean).join(' · ')} sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={6}><RePicker path="/re/units" label="Unit (optional)" value={v.unit_id} onChange={set('unit_id')} getLabel={(u) => [u.unit_number, u.project_name].filter(Boolean).join(' · ')} sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={6}>{money('advisor_fee_amount', 'Advisor fee')}</Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} size="small" label="Notes" value={v.notes || ''} onChange={set('notes')} /></Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <MortgageMetrics values={v} lender={lender.data} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

MortgageForm.propTypes = {
  open: PropTypes.bool.isRequired,
  application: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
MortgageForm.defaultProps = { application: null };
